import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import config from '../../config';
import { formatCurrency } from '../../utils/formatCurrency';
import './overlay.css';

const MOCK_TIPS = [
  { id: 1, name: 'StreamFan99', amount: 2500, message: 'Keep up the amazing content!' },
  { id: 2, name: 'Castle Builder', amount: 1000, message: null },
  { id: 3, name: 'Anonymous', amount: 500, message: 'Love the castle!' },
  { id: 4, name: 'xXDragonSlayerXx', amount: 5000, message: "First brick placed — let's gooo" },
  { id: 5, name: 'TwitchPrime', amount: 200, message: null },
  { id: 6, name: 'NightOwl', amount: 750, message: 'Watching every stream 🏰' },
];

const MOCK_ALERT_DONORS = [
  { name: 'StreamFan99', amount: 2500, message: 'Keep up the amazing content!' },
  { name: 'Castle Builder', amount: 1000, message: null },
  { name: 'xXDragonSlayerXx', amount: 5000, message: "First brick placed — let's gooo" },
  { name: 'NightOwl', amount: 750, message: null },
];

// ─── Recent Tips layer ────────────────────────────────────────────────────────
function RecentTipsLayer({ layerId, creatorId, testMode, initialConfig }) {
  const [donations, setDonations] = useState(testMode ? MOCK_TIPS : []);
  // Baseline from the config already loaded with the scene, so a trigger left
  // over from an earlier test session doesn't replay itself on page load.
  const liveTriggerRef = useRef((initialConfig || {}).test_trigger ?? 0);

  useEffect(() => {
    if (testMode || !creatorId) return;
    const fetchDonations = async () => {
      try {
        const res = await fetch(
          `${config.API_URL}/donations/recent?creator_id=${creatorId}&limit=10&completed_only=true`
        );
        if (res.ok) setDonations(await res.json());
      } catch (err) {
        console.error('RecentTipsLayer fetch error:', err);
      }
    };
    fetchDonations();
    const interval = setInterval(fetchDonations, 10000);
    return () => clearInterval(interval);
  }, [creatorId, testMode]);

  // Live mode: poll for a test donation pushed from the editor
  useEffect(() => {
    if (testMode || !layerId) return;
    const check = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/recent-tips/${layerId}`);
        if (!res.ok) return;
        const data = await res.json();
        const trigger = data.test_trigger ?? 0;
        if (trigger > liveTriggerRef.current && data.test_donation) {
          liveTriggerRef.current = trigger;
          const d = data.test_donation;
          setDonations((prev) => [
            { id: `test-${trigger}`, name: d.name, amount: d.amount, message: d.message },
            ...prev,
          ].slice(0, 10));
        }
      } catch (_) {}
    };
    const interval = setInterval(check, 3000);
    return () => clearInterval(interval);
  }, [layerId, testMode]);

  return (
    <div style={{ width: '100%', height: '100%', padding: '12px', boxSizing: 'border-box', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {donations.length === 0 ? (
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textAlign: 'center', marginTop: 16 }}>
          No recent tips
        </p>
      ) : (
        donations.slice(0, 8).map((d) => (
          <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '6px 10px' }}>
            <span style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 600 }}>{d.name || 'Anonymous'}</span>
            <span style={{ color: '#34d399', fontSize: 13, fontWeight: 700 }}>{formatCurrency(d.amount)}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Tip Alert layer ──────────────────────────────────────────────────────────
function TipAlertLayer({ layerId, creatorId, testMode, testTrigger, onTipFired, initialConfig }) {
  const [currentAlert, setCurrentAlert] = useState(null);
  const [visible, setVisible] = useState(false);
  const lastSeenIdRef = useRef(null);
  const alertQueueRef = useRef([]);
  const isShowingRef = useRef(false);
  const mockIndexRef = useRef(0);
  // Baseline from the config already loaded with the scene, so a trigger left
  // over from an earlier test session doesn't replay itself on page load.
  const liveTriggerRef = useRef((initialConfig || {}).test_tip_trigger ?? 0);

  const showNextAlert = () => {
    if (alertQueueRef.current.length === 0) {
      isShowingRef.current = false;
      return;
    }
    isShowingRef.current = true;
    const next = alertQueueRef.current.shift();
    setCurrentAlert(next);
    setVisible(true);
    onTipFired?.();
    setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentAlert(null);
        showNextAlert();
      }, 600);
    }, 8000);
  };

  const queueAlert = (donation) => {
    alertQueueRef.current.push(donation);
    if (!isShowingRef.current) showNextAlert();
  };

  // Test mode: fire when testTrigger increments
  useEffect(() => {
    if (!testMode || testTrigger === 0) return;
    const donor = MOCK_ALERT_DONORS[mockIndexRef.current % MOCK_ALERT_DONORS.length];
    mockIndexRef.current += 1;
    queueAlert({ ...donor, id: `test-${testTrigger}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testTrigger]);

  // Live mode: poll for test tip trigger from editor
  useEffect(() => {
    if (testMode || !layerId) return;
    const check = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/tip-alert/${layerId}`);
        if (!res.ok) return;
        const data = await res.json();
        const trigger = data.test_tip_trigger ?? 0;
        if (trigger > liveTriggerRef.current) {
          liveTriggerRef.current = trigger;
          const donor = MOCK_ALERT_DONORS[mockIndexRef.current % MOCK_ALERT_DONORS.length];
          mockIndexRef.current += 1;
          queueAlert({ ...donor, amount: data.test_tip_amount ?? 5, id: `live-test-${trigger}` });
        }
      } catch (_) {}
    };
    const interval = setInterval(check, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId, testMode]);

  // Live mode: poll for new donations
  useEffect(() => {
    if (testMode || !creatorId) return;

    const init = async () => {
      try {
        const res = await fetch(`${config.API_URL}/donations/recent?creator_id=${creatorId}&limit=1&completed_only=true`);
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) lastSeenIdRef.current = data[0].id;
        }
      } catch (err) {
        console.error('TipAlertLayer init error:', err);
      }
    };

    init();

    const checkNew = async () => {
      try {
        const res = await fetch(`${config.API_URL}/donations/recent?creator_id=${creatorId}&limit=5&completed_only=true`);
        if (res.ok) {
          const data = await res.json();
          const newOnes = data.filter((d) => lastSeenIdRef.current === null || d.id > lastSeenIdRef.current);
          newOnes.reverse().forEach((d) => queueAlert(d));
          if (data.length > 0) lastSeenIdRef.current = data[0].id;
        }
      } catch (err) {
        console.error('TipAlertLayer poll error:', err);
      }
    };

    const interval = setInterval(checkNew, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, testMode]);

  if (!currentAlert) return null;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.5s ease', opacity: visible ? 1 : 0 }}>
      <div style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.9), rgba(59,130,246,0.9))', borderRadius: 12, padding: '16px 24px', textAlign: 'center', boxShadow: '0 0 30px rgba(6,182,212,0.5)', maxWidth: '90%' }}>
        <p style={{ color: '#fff', fontSize: 13, fontWeight: 600, marginBottom: 4, opacity: 0.85 }}>New Tip!</p>
        <p style={{ color: '#fff', fontSize: 28, fontWeight: 800, margin: '4px 0' }}>{formatCurrency(currentAlert.amount)}</p>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>from <strong>{currentAlert.name || 'Anonymous'}</strong></p>
        {currentAlert.message && (
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 8, fontStyle: 'italic' }}>
            "{currentAlert.message.length > 120 ? currentAlert.message.slice(0, 120) + '…' : currentAlert.message}"
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Brick Counter layer ───────────────────────────────────────────────────────
function BrickCounterLayer({ layerId, username, testMode, testBricks, testGoal, initialConfig }) {
  const [bricksUsed, setBricksUsed] = useState(null);
  const [goal, setGoal] = useState(null);
  // {bricks, goal} pushed from the editor -- starts from whatever was already
  // persisted with the scene, so a live browser-source reload reflects the
  // current test state (same as the subathon timer), not a blank one.
  const initTestBricks = (initialConfig || {}).test_bricks;
  const [liveOverride, setLiveOverride] = useState(
    initTestBricks === null || initTestBricks === undefined
      ? null
      : { bricks: initTestBricks, goal: (initialConfig || {}).test_goal }
  );
  const liveTriggerRef = useRef((initialConfig || {}).test_trigger ?? 0);

  useEffect(() => {
    if (testMode || !username) return;
    const fetch_ = async () => {
      try {
        const res = await fetch(`${config.API_URL}/creators/castle-public/${username}`);
        if (res.ok) {
          const data = await res.json();
          setBricksUsed(data.bricks_used ?? 0);
          setGoal(data.castle_goal ?? null);
        }
      } catch (err) {
        console.error('BrickCounterLayer fetch error:', err);
      }
    };
    fetch_();
    const interval = setInterval(fetch_, 30000);
    return () => clearInterval(interval);
  }, [username, testMode]);

  // Live mode: poll for a test bricks value pushed from the editor
  useEffect(() => {
    if (testMode || !layerId) return;
    const check = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/brick-counter/${layerId}`);
        if (!res.ok) return;
        const data = await res.json();
        const trigger = data.test_trigger ?? 0;
        if (trigger > liveTriggerRef.current) {
          liveTriggerRef.current = trigger;
          setLiveOverride(
            data.test_bricks === null || data.test_bricks === undefined
              ? null
              : { bricks: data.test_bricks, goal: data.test_goal }
          );
        }
      } catch (_) {}
    };
    check();
    const interval = setInterval(check, 3000);
    return () => clearInterval(interval);
  }, [layerId, testMode]);

  const displayBricks = testMode ? testBricks : (liveOverride ? liveOverride.bricks : bricksUsed);
  const displayGoal = testMode ? testGoal : (liveOverride ? (liveOverride.goal ?? goal) : goal);

  if (displayBricks === null) return null;

  const pct = displayGoal ? Math.min((displayBricks / displayGoal) * 100, 100) : null;

  return (
    <div style={{ width: '100%', height: '100%', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '12px 16px', boxSizing: 'border-box' }}>
      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Bricks</span>
      <span style={{ color: '#fff', fontSize: 42, fontWeight: 800, lineHeight: 1 }}>{displayBricks.toLocaleString()}</span>
      {pct !== null && (
        <div style={{ width: '100%', marginTop: 6 }}>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.12)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #06b6d4, #3b82f6)', borderRadius: 4, transition: 'width 0.5s ease' }} />
          </div>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 3, display: 'block', textAlign: 'center' }}>
            {displayBricks} / {displayGoal} goal
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Subathon Timer layer ──────────────────────────────────────────────────────
function SubathonTimerLayer({ layerId, initialConfig, testMode, testAddSecs }) {
  const computeSecs = (cfg) => {
    let secs = parseFloat(cfg.seconds_remaining ?? 0);
    if (cfg.is_running && cfg.last_started_at) {
      try {
        const started = new Date(cfg.last_started_at).getTime() / 1000;
        secs = Math.max(0, secs - (Date.now() / 1000 - started));
      } catch (_) {}
    }
    return secs;
  };

  const fmtTime = (totalSecs) => {
    const s = Math.round(Math.max(0, totalSecs));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const cfgRef = useRef(initialConfig || {});
  const lastTipAtRef = useRef((initialConfig || {}).last_tip_at || null);
  const prevTestAddRef = useRef(testAddSecs || 0);

  const initSecs = testMode
    ? parseFloat((initialConfig || {}).start_time_minutes ?? 60) * 60
    : computeSecs(initialConfig || {});
  const [displaySecs, setDisplaySecs] = useState(initSecs);
  const [chip, setChip] = useState(null);

  const showChip = useCallback((secs, cfg) => {
    if ((cfg || cfgRef.current).show_event_info === false) return;
    setChip(secs);
    setTimeout(() => setChip(null), 5000);
  }, []);

  // 1-second ticker
  useEffect(() => {
    const tick = setInterval(() => {
      if (testMode) {
        setDisplaySecs((s) => Math.max(0, s - 1));
      } else {
        setDisplaySecs(computeSecs(cfgRef.current));
      }
    }, 1000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMode]);

  // Poll server for state updates (live only)
  useEffect(() => {
    if (testMode || !layerId) return;
    const poll = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/subathon/${layerId}`);
        if (!res.ok) return;
        const newCfg = await res.json();
        cfgRef.current = newCfg;
        if (newCfg.last_tip_at && newCfg.last_tip_at !== lastTipAtRef.current) {
          lastTipAtRef.current = newCfg.last_tip_at;
          showChip(newCfg.last_time_added_secs || 0, newCfg);
        }
      } catch (_) {}
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => clearInterval(iv);
  }, [layerId, testMode, showChip]);

  // Auto-start public endpoint (live only)
  useEffect(() => {
    if (testMode || !layerId) return;
    fetch(`${config.API_URL}/overlay-scenes/subathon/${layerId}/auto-start`, { method: 'POST' }).catch(() => {});
  }, [layerId, testMode]);

  // Test mode: respond to tip fire
  useEffect(() => {
    if (!testMode || testAddSecs === prevTestAddRef.current) return;
    const added = testAddSecs - prevTestAddRef.current;
    prevTestAddRef.current = testAddSecs;
    setDisplaySecs((s) => s + added);
    showChip(added, cfgRef.current);
  }, [testAddSecs, testMode, showChip]);

  const cfg = cfgRef.current;
  const isOver = displaySecs <= 0;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <div style={{
        background: 'rgba(26,29,33,0.98)',
        borderRadius: cfg.corner_radius ?? 24,
        padding: '16px 32px',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        border: '2px solid #99C2FF',
        boxShadow: '0 0 40px rgba(153,194,255,0.3), 0 0 80px rgba(153,194,255,0.1), 0 10px 40px rgba(0,0,0,0.5)',
        backdropFilter: 'blur(20px)',
      }}>
        {chip != null && (
          <div className="subathon-chip" style={{
            position: 'absolute',
            top: -32,
            left: '50%',
            background: '#99C2FF',
            color: '#0A0B0D',
            borderRadius: 20,
            padding: '3px 12px',
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}>
            +{fmtTime(chip)}
          </div>
        )}
        <p style={{ color: '#99C2FF', fontSize: 12, fontWeight: 600, marginBottom: 4, letterSpacing: '0.12em', textTransform: 'uppercase', textShadow: '0 0 10px rgba(153,194,255,0.5)' }}>
          {isOver ? 'Subathon Over' : 'Subathon'}
        </p>
        <span style={{
          fontSize: cfg.timer_size || 40,
          color: isOver ? '#99C2FF' : (cfg.timer_color || '#ffffff'),
          fontWeight: 800,
          fontFamily: "'Montserrat', monospace",
          lineHeight: 1.1,
          textShadow: isOver ? '0 0 20px rgba(153,194,255,0.5)' : undefined,
        }}>
          {fmtTime(displaySecs)}
        </span>
      </div>
    </div>
  );
}

// ─── Test Control Panel ───────────────────────────────────────────────────────
function TestPanel({ bricks, goal, onAddBricks, onReset, onFireAlert, onFireSubathonTip, subathonMinPerDollar }) {
  const btnBase = {
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 12,
    padding: '5px 10px',
    transition: 'opacity 0.15s',
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      right: 16,
      zIndex: 9999,
      background: 'rgba(10,15,26,0.95)',
      border: '1px solid rgba(234,179,8,0.4)',
      borderRadius: 10,
      padding: '12px 14px',
      minWidth: 220,
      fontFamily: 'system-ui, sans-serif',
      color: '#fff',
      boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
    }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#eab308', marginBottom: 10 }}>
        Test Controls
      </p>

      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
        Bricks: <span style={{ color: '#fff', fontWeight: 700 }}>{bricks.toLocaleString()}</span>
        {goal ? <span style={{ color: 'rgba(255,255,255,0.35)' }}> / {goal}</span> : null}
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[1, 5, 10, 50, 100].map((n) => (
          <button key={n} style={{ ...btnBase, background: 'rgba(6,182,212,0.2)', color: '#67e8f9', border: '1px solid rgba(6,182,212,0.3)' }}
            onClick={() => onAddBricks(n)}>
            +{n}
          </button>
        ))}
        <button style={{ ...btnBase, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
          onClick={onReset}>
          Reset
        </button>
      </div>

      {onFireAlert && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
          <button
            style={{ ...btnBase, background: 'rgba(139,92,246,0.2)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)', width: '100%' }}
            onClick={onFireAlert}
          >
            Fire tip alert
          </button>
        </div>
      )}
      {onFireSubathonTip && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, marginTop: 4 }}>
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>
            Subathon — {subathonMinPerDollar ?? 1} min / $1
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[1, 5, 10, 20].map((amt) => (
              <button key={amt} style={{ ...btnBase, background: 'rgba(34,211,238,0.15)', color: '#67e8f9', border: '1px solid rgba(34,211,238,0.3)' }}
                onClick={() => onFireSubathonTip(amt)}>
                ${amt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tip Media layer ──────────────────────────────────────────────────────────
function TipMediaLayer({ layerId, mediaUrl, tipFireCount, testMode, initialConfig }) {
  const videoRef = useRef(null);
  const prevCountRef = useRef(0);
  // Baseline from the config already loaded with the scene, so a trigger left
  // over from an earlier test session doesn't replay itself on page load.
  const liveTriggerRef = useRef((initialConfig || {}).test_tip_trigger ?? 0);

  const playMedia = useCallback(() => {
    if (!mediaUrl) return;
    if (/\.webm$/i.test(mediaUrl)) {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
    } else {
      try { new Audio(mediaUrl).play().catch(() => {}); } catch (e) {}
    }
  }, [mediaUrl]);

  // Fired via a scene-level tip (real donation or a tip_alert layer's test fire)
  useEffect(() => {
    if (tipFireCount <= 0 || tipFireCount === prevCountRef.current) return;
    prevCountRef.current = tipFireCount;
    playMedia();
  }, [tipFireCount, playMedia]);

  // Live mode: poll this layer's own test-tip trigger, so it can be tested
  // even when the scene has no tip_alert layer
  useEffect(() => {
    if (testMode || !layerId) return;
    const check = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/tip-alert/${layerId}`);
        if (!res.ok) return;
        const data = await res.json();
        const trigger = data.test_tip_trigger ?? 0;
        if (trigger > liveTriggerRef.current) {
          liveTriggerRef.current = trigger;
          playMedia();
        }
      } catch (_) {}
    };
    const interval = setInterval(check, 3000);
    return () => clearInterval(interval);
  }, [layerId, testMode, playMedia]);

  if (!mediaUrl || !/\.webm$/i.test(mediaUrl)) return null;

  return (
    <video
      ref={videoRef}
      src={mediaUrl}
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  );
}

// ─── Scene Renderer ────────────────────────────────────────────────────────────
export default function SceneRenderer() {
  const { sceneId } = useParams(); // username param is decorative; sceneId drives the fetch
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get('test') === '1';

  const [scene, setScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });

  // Test state
  const [testBricks, setTestBricks] = useState(0);
  const testGoal = 500;
  const [testAlertTrigger, setTestAlertTrigger] = useState(0);
  const [testSubathonAddSecs, setTestSubathonAddSecs] = useState(0);

  // Tip fire counter — incremented on each tip, consumed by TipMediaLayer(s)
  const [tipFireCount, setTipFireCount] = useState(0);

  // Scene-level sound_url backward compat (audio only; WebM now handled by tip_media layers)
  const videoRef = useRef(null);
  const [videoVisible, setVideoVisible] = useState(false);

  const handleTipFired = useCallback(() => {
    setTipFireCount((n) => n + 1);
    const url = scene?.sound_url;
    if (!url) return;
    if (/\.webm$/i.test(url)) {
      setVideoVisible(true);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
    } else {
      try { new Audio(url).play().catch(() => {}); } catch (e) {}
    }
  }, [scene?.sound_url]);

  useEffect(() => {
    const fetchScene = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/public/${sceneId}`);
        if (res.ok) {
          const data = await res.json();
          setScene(data);
        }
      } catch (err) {
        console.error('SceneRenderer fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScene();
  }, [sceneId]);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (loading || !scene) return null;

  const bgColor =
    scene.background === 'transparent' ? 'transparent'
    : scene.background === 'white' ? '#ffffff'
    : scene.background === 'black' ? '#000000'
    : 'transparent';

  const sortedLayers = [...(scene.layers || [])].sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
  const hasTestLayer = (type) => testMode && sortedLayers.some((l) => l.type === type);
  const subathonLayer = testMode ? sortedLayers.find((l) => l.type === 'subathon_timer') : null;

  // Always scale to fit the viewport — at 1920×1080 (OBS) this is exactly 1.0
  const scale = Math.min(viewport.w / scene.width, viewport.h / scene.height);

  const sceneContent = (
    <div style={{
      position: 'relative',
      width: scene.width,
      height: scene.height,
      background: bgColor,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {testMode && (
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(234,179,8,0.9)', color: '#000', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, zIndex: 9999 }}>
          Test mode
        </div>
      )}

      {scene.sound_url && /\.webm$/i.test(scene.sound_url) && (
        <video
          ref={videoRef}
          src={scene.sound_url}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '80%',
            maxHeight: '80%',
            display: videoVisible ? 'block' : 'none',
            zIndex: 9998,
            borderRadius: 8,
          }}
          onEnded={() => setVideoVisible(false)}
        />
      )}

      {sortedLayers.map((layer) => (
        <div key={layer.id} style={{ position: 'absolute', left: layer.x, top: layer.y, width: layer.width, height: layer.height, overflow: 'hidden' }}>
          {layer.type === 'recent_tips' && (
            <RecentTipsLayer layerId={layer.id} creatorId={scene.user_id} testMode={testMode} initialConfig={layer.config || {}} />
          )}
          {layer.type === 'tip_alert' && (
            <TipAlertLayer layerId={layer.id} creatorId={scene.user_id} testMode={testMode} testTrigger={testAlertTrigger} onTipFired={handleTipFired} initialConfig={layer.config || {}} />
          )}
          {layer.type === 'brick_counter' && (
            <BrickCounterLayer layerId={layer.id} username={scene.creator_username} testMode={testMode} testBricks={testBricks} testGoal={testGoal} initialConfig={layer.config || {}} />
          )}
          {layer.type === 'tip_media' && (
            <TipMediaLayer layerId={layer.id} mediaUrl={layer.config?.media_url} tipFireCount={tipFireCount} testMode={testMode} initialConfig={layer.config || {}} />
          )}
          {layer.type === 'subathon_timer' && (
            <SubathonTimerLayer
              layerId={layer.id}
              initialConfig={layer.config || {}}
              testMode={testMode}
              testAddSecs={testSubathonAddSecs}
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
        {sceneContent}
      </div>

      {testMode && (hasTestLayer('brick_counter') || hasTestLayer('tip_alert') || hasTestLayer('subathon_timer')) && (
        <TestPanel
          bricks={testBricks}
          goal={hasTestLayer('brick_counter') ? testGoal : null}
          onAddBricks={(n) => setTestBricks((b) => b + n)}
          onReset={() => setTestBricks(0)}
          onFireAlert={hasTestLayer('tip_alert') ? () => setTestAlertTrigger((n) => n + 1) : null}
          onFireSubathonTip={subathonLayer ? (dollarAmt) => {
            const minsPerDollar = parseFloat(subathonLayer.config?.minutes_per_dollar ?? 1);
            setTestSubathonAddSecs((s) => s + minsPerDollar * dollarAmt * 60);
          } : null}
          subathonMinPerDollar={subathonLayer?.config?.minutes_per_dollar ?? 1}
        />
      )}
    </div>
  );
}
